import React from 'react'
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  MoreHorizontal,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { PageSizeSelect } from '@/components/reusable/PageSizeSelect'
import { buildPageWindow, buildSlidingPageWindow } from '@/lib/pagination/pageWindow'

interface ContactsPaginationProps {
  currentPage: number
  totalPages: number
  totalCount: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  isLoading?: boolean
  itemLabel?: string // Optional label for items (defaults to "contacts")
  pageSizeOptions?: number[] // Optional custom page size options
  minPageSize?: number // Optional minimum page size
  hideTotalCount?: boolean // Hide the "of X" total count (useful for cursor-based APIs)
  hidePageSizeSelector?: boolean
  /** Keep the bar visible even when everything fits on one page. */
  alwaysShow?: boolean
  /**
   * 'anchored' (the default) keeps first and last in view: `1 … 4 5 6 … 68`.
   * 'sliding' shows three numbers that follow the user: `1 2 3`, then `3 4 5`.
   */
  pageWindow?: 'anchored' | 'sliding'
  className?: string
  testId?: string
  pageSizeTestId?: string
}

const navBtn =
  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[#E4E7EC] bg-white text-[#475467] transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white'

export function ContactsPagination({
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  onPageSizeChange,
  isLoading = false,
  itemLabel = 'results',
  pageSizeOptions = [10, 20],
  minPageSize = 10,
  hideTotalCount = false,
  hidePageSizeSelector = false,
  alwaysShow = false,
  pageWindow = 'anchored',
  className,
  testId = 'contacts-pagination',
  pageSizeTestId = 'contacts-page-size',
}: ContactsPaginationProps) {
  const startItem = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0
  const endItem = Math.min(currentPage * pageSize, totalCount)
  const pages =
    pageWindow === 'sliding'
      ? buildSlidingPageWindow(currentPage, totalPages)
      : buildPageWindow(currentPage, totalPages)
  const atFirst = currentPage <= 1
  const atLast = currentPage >= totalPages || totalPages < 1

  const handlePageChange = (page: number) => {
    if (isLoading) return
    if (page >= 1 && page <= totalPages) onPageChange(page)
  }

  const handlePageSizeChange = (newSize: number) => {
    const finalSize = minPageSize ? Math.max(newSize, minPageSize) : newSize
    onPageSizeChange(finalSize)
  }

  // A single page that already fits has nothing to page through — hide the whole bar.
  if (!alwaysShow && totalPages <= 1 && totalCount <= pageSize) return null

  return (
    <div
      data-testid={testId}
      className={cn(
        'flex w-full flex-nowrap items-center justify-between gap-3 overflow-x-auto border-t border-[#F2F4F7] px-5 py-3',
        className,
      )}
    >
      <div className="flex shrink-0 flex-nowrap items-center gap-x-4">
        <span className="text-[12.5px] tabular-nums text-[#6B7280]">
          {isLoading ? (
            '…'
          ) : hideTotalCount ? (
            <>
              Showing <span className="font-semibold text-[#101828]">{startItem}</span>–
              <span className="font-semibold text-[#101828]">{endItem}</span> {itemLabel}
            </>
          ) : (
            <>
              Showing <span className="font-semibold text-[#101828]">{startItem}</span>–
              <span className="font-semibold text-[#101828]">{endItem}</span> of{' '}
              <span className="font-semibold text-[#101828]">{totalCount}</span> {itemLabel}
            </>
          )}
        </span>

        {!hidePageSizeSelector && (
          <label className="flex items-center gap-2 text-[12.5px] text-[#6B7280]">
            <span>Per page</span>
            <PageSizeSelect
              value={pageSize}
              options={pageSizeOptions}
              onChange={handlePageSizeChange}
              disabled={isLoading}
              data-testid={pageSizeTestId}
            />
          </label>
        )}
      </div>

      {totalPages > 1 ? (
        <nav
          aria-label="Pagination"
          className="flex shrink-0 flex-nowrap items-center gap-1"
        >
          <button
            type="button"
            onClick={() => handlePageChange(1)}
            disabled={atFirst || isLoading}
            aria-label="First page"
            className={navBtn}
          >
            <ChevronsLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={atFirst || isLoading}
            aria-label="Previous page"
            className={navBtn}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          {pages.map((page, index) =>
            page === 'ellipsis' ? (
              <span
                key={`ellipsis-${index}`}
                aria-hidden
                className="flex h-8 w-8 items-center justify-center text-[#98A2B3]"
              >
                <MoreHorizontal className="h-4 w-4" />
              </span>
            ) : (
              <button
                key={page}
                type="button"
                onClick={() => handlePageChange(page)}
                disabled={isLoading}
                aria-label={`Go to page ${page}`}
                aria-current={currentPage === page ? 'page' : undefined}
                className={cn(
                  'inline-flex h-8 min-w-[2rem] shrink-0 items-center justify-center rounded-lg border px-2 text-[12.5px] font-semibold tabular-nums transition disabled:cursor-not-allowed disabled:opacity-40',
                  currentPage === page
                    ? 'border-brand bg-brand-soft text-brand-cta'
                    : 'border-[#E4E7EC] bg-white text-[#475467] hover:bg-slate-50',
                )}
              >
                {page}
              </button>
            ),
          )}

          <button
            type="button"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={atLast || isLoading}
            aria-label="Next page"
            className={navBtn}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => handlePageChange(totalPages)}
            disabled={atLast || isLoading}
            aria-label="Last page"
            className={navBtn}
          >
            <ChevronsRight className="h-4 w-4" />
          </button>
        </nav>
      ) : null}
    </div>
  )
}
